import React, { forwardRef } from 'react'
import styled from 'styled-components'

import Dialog from '@mui/material/Dialog'

import KeyboardAccessible from 'components/KeyboardAccessible'

import { color } from 'styles/colors'

import useScreenSize from 'hooks/useScreenSize'

type Ref = HTMLDivElement

type Props = React.PropsWithChildren<{
  closeCallback: () => void
  showCloseButton?: boolean
  title?: string | null
  isDisplayed?: boolean
  variant?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  className?: string
  fullWidth?: boolean
  fullScreen?: boolean
  scroll?: 'body' | 'paper'
  dataTestID?: string
}>

const Modal = forwardRef<Ref, Props>(({
  title = null,
  showCloseButton = true,
  children,
  closeCallback,
  isDisplayed = false,
  fullWidth = true,
  fullScreen = false,
  variant = 'md',
  className = '',
  scroll = 'body',
  dataTestID = ''
}: Props, ref) => {
  const screenSize = useScreenSize()

  return (
    <Dialog
      ref={ref}
      open={isDisplayed}
      onClose={closeCallback}
      scroll={scroll}
      fullScreen={fullScreen || screenSize === 'small'}
      fullWidth={fullWidth}
      maxWidth={variant}
      PaperProps={{ sx: { borderRadius: '5px' } }}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      disableRestoreFocus
    >
      <S.Content {...{ className }}>
        {
          showCloseButton && (
            <S.CloseButton onClick={closeCallback}>
              <S.CloseIcon role="button" aria-label="Close" src="/img/close.svg" data-testid={dataTestID} />
            </S.CloseButton>
          )
        }
        {title && <S.Title>{title}</S.Title> }
        {children}
      </S.Content>
    </Dialog>
  )
})

export default Modal

const S = {
  Title: styled.h1`
    color: ${color('primaryText')};
    font-size: 24px;
    font-weight: 600;
    text-transform: capitalize;
  `,
  Content: styled.div`
    padding: 25px;
    background: ${color('paneBackground')};
  `,
  CloseButton: styled(KeyboardAccessible)`
    float: right;
    padding: 10px;
    margin: -10px;
    height: 30px;
  `,
  CloseIcon: styled.img`
    height: 25px;
    width: 25px;
    margin: -10px -10px 5px -5px;
    cursor: pointer;
  `
}
